<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import i18n from '../../plugins/i18n';

export default {
  name: 'kalio-button',
  props: {
    label: { type: String, default: null },
    leftIconClasses: { type: String, default: 'mr-1.5 fill-current' },
    rightIconClasses: { type: String, default: 'ml-1.5 fill-current' },
    labelClasses: { type: String, default: '' },
    leftIcon: { type: String, default: null },
    rightIcon: { type: String, default: null },
    alternativeTag: { type: String, default: null },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => [
        'primary',
        'secondary',
        'tertiary',
        'blue',
        'custom',
        'text',
        'red',
        'disabled',
        'transparent',
      ].includes(value),
    },
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'small', 'big'].includes(value),
    },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: i18n.global.t('form.loading') },
  },
  setup(_, { attrs }) {
    const tagRef = ref(null);

    function captureClickEvent(e) {
      if (attrs.href && attrs.disabled) e.preventDefault();
    }

    onMounted(() => tagRef.value.addEventListener('click', captureClickEvent, true));
    onBeforeUnmount(() => tagRef.value.removeEventListener('click', captureClickEvent, true));

    return {
      tagRef,
    };
  },
  computed: {
    tag() {
      if (this.alternativeTag) return this.alternativeTag;

      return this.$attrs.href ? 'a' : 'button';
    },
    colorClasses() {
      const disabledClasses = {
        primary: 'cursor-not-allowed bg-pv-yellow text-blue-gray-800 opacity-75',
        secondary: 'cursor-not-allowed bg-blue-gray-700 text-blue-gray-300 opacity-90',
        tertiary: 'cursor-not-allowed bg-blue-gray-50/5 text-blue-gray-400' +
                  ' border border-blue-gray-500',
        blue: 'cursor-not-allowed bg-pv-light-blue/10 text-pv-light-blue/75' +
              ' border border-pv-light-blue/50',
        red: 'cursor-not-allowed bg-red-400/10 text-red-400/75 border border-red-400/50',
        text: 'cursor-not-allowed text-pv-yellow',
        custom: 'cursor-not-allowed',
      };

      const classes = {
        primary: 'bg-pv-yellow text-black hover:bg-opacity-75 focus:ring-pv-yellow',
        secondary: 'bg-blue-gray-500 text-blue-gray-50 hover:bg-opacity-80 focus:ring-blue-gray-500',
        tertiary: 'bg-blue-gray-50/5 hover:bg-blue-gray-50/10 hover:border-blue-gray-400' +
                  ' border border-blue-gray-500 focus:ring-blue-gray-500 text-blue-gray-100',
        blue: 'bg-pv-light-blue bg-opacity-10 hover:bg-opacity-100 hover:text-blue-gray-50 ' +
              'border border-pv-light-blue text-pv-light-blue',
        text: 'text-pv-yellow focus:ring-pv-yellow',
        red: 'bg-red-400 bg-opacity-10 hover:bg-opacity-100 hover:text-blue-gray-50 ' +
              'border border-red-400 text-red-400 focus:ring-red-400',
        custom: '',
        disabled: disabledClasses[this.variant] || disabledClasses.primary,
      };

      return this.$attrs.disabled ? classes.disabled : (classes[this.variant] || this.variant);
    },
    sizeClasses() {
      const classes = {
        default: 'px-3 py-1.5 text-base',
        small: 'px-3 py-1.5 text-sm',
        big: 'px-8 py-2 text-xl',
      };

      return classes[this.size] || this.size;
    },
  },
};
</script>

<template>
  <component
    :is="tag"
    ref="tagRef"
    class="flex items-center justify-center whitespace-nowrap rounded-md focus:outline-none"
    :class="[
      colorClasses,
      sizeClasses,
      { 'cursor-pointer focus:ring-2 focus:ring-offset-2         focus:ring-offset-pv-blue-800': tag === 'button' && !$attrs.disabled }
    ]"
  >
    <template v-if="loading">
      <svg
        role="status"
        class="mr-2 inline size-4 animate-spin text-blue-gray-50"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="#0F172A"
        />
      </svg>
      {{ loadingText }}
    </template>
    <template v-else>
      <inline-svg
        v-if="leftIcon"
        :src="require(`assets/images/icons/${leftIcon}`)"
        :class="leftIconClasses"
      />
      <span
        v-if="label"
        :class="labelClasses"
      >
        {{ label }}
      </span>
      <slot v-else />
      <inline-svg
        v-if="rightIcon"
        :src="require(`assets/images/icons/${rightIcon}`)"
        :class="rightIconClasses"
      />
    </template>
  </component>
</template>
